import React, { useMemo } from 'react';

const UTM_SOURCE_COLORS = {
  google: { bg: 'rgba(66, 133, 244, 0.1)', color: '#4285f4', dot: '#4285f4' },
  facebook: { bg: 'rgba(24, 119, 242, 0.1)', color: '#1877f2', dot: '#1877f2' },
  instagram: { bg: 'rgba(225, 48, 108, 0.1)', color: '#e1306c', dot: '#e1306c' },
  webinar: { bg: 'rgba(5, 150, 105, 0.1)', color: '#059669', dot: '#059669' },
  direct: { bg: 'rgba(107, 122, 141, 0.1)', color: '#6b7a8d', dot: '#6b7a8d' },
  email: { bg: 'rgba(245, 158, 11, 0.1)', color: '#d97706', dot: '#d97706' },
};

const UtmDropOffTable = ({ bookings, funnelByUtm }) => {
  const rows = useMemo(() => {
    const sourceMap = {};
    bookings?.forEach(b => {
      const src = b?.utm_source || 'direct';
      if (!sourceMap?.[src]) sourceMap[src] = { source: src, bookings: 0, sessions: 0 };
      sourceMap[src].bookings += 1;
    });
    Object.keys(sourceMap)?.forEach(src => {
      const fData = funnelByUtm?.[src];
      const multiplier = 2 + (src === 'google' ? 3 : src === 'facebook' ? 2.5 : src === 'webinar' ? 1.5 : 2);
      sourceMap[src].sessions = fData?.sessions ?? Math.round(sourceMap?.[src]?.bookings * multiplier);
    });
    return Object.values(sourceMap)?.map(row => ({
        ...row,
        convRate: row?.sessions > 0 ? ((row?.bookings / row?.sessions) * 100)?.toFixed(1) : '0.0',
        dropRate: row?.sessions > 0 ? (((row?.sessions - row?.bookings) / row?.sessions) * 100)?.toFixed(1) : '0.0',
      }))?.sort((a, b) => parseFloat(b?.convRate) - parseFloat(a?.convRate));
  }, [bookings, funnelByUtm]);

  const getSourceStyle = (src) =>
    UTM_SOURCE_COLORS?.[src?.toLowerCase()] || { bg: 'rgba(62,73,84,0.08)', color: '#3E4954', dot: '#3E4954' };

  const getConvColor = (rate) => {
    const r = parseFloat(rate);
    if (r >= 25) return '#16a34a';
    if (r >= 15) return '#d97706';
    return '#dc2626';
  };

  const getDropColor = (rate) => {
    const r = parseFloat(rate);
    if (r <= 60) return '#16a34a';
    if (r <= 80) return '#d97706';
    return '#dc2626';
  };

  return (
    <div style={{ background: 'white', borderRadius: '20px', border: '1px solid #dde1e7', boxShadow: '0 4px 6px rgba(62,73,84,0.04), 0 10px 20px rgba(62,73,84,0.06)', overflow: 'hidden', marginBottom: '24px' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #dde1e7', background: '#3E4954', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <line x1='18' y1='20' x2='18' y2='10'/><line x1='12' y1='20' x2='12' y2='4'/><line x1='6' y1='20' x2='6' y2='14'/>
          </svg>
        </div>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '16px', fontWeight: 700, color: 'white', margin: 0 }}>Abbruchrate nach UTM Source</h2>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', fontFamily: 'var(--font-ui)', margin: 0 }}>Conversion und Drop-off je Kanal</p>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)' }}>
          <thead>
            <tr style={{ background: '#f5f6f8' }}>
              {['UTM Source', 'Sitzungen', 'Buchungen', 'Conversion Rate', 'Abbruchrate', 'Bewertung']?.map(col => (
                <th key={col} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7a8d', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7a8d', fontSize: '14px' }}>Keine Daten verfuegbar.</td></tr>
            ) : (
              rows?.map((row, i) => {
                const sc = getSourceStyle(row?.source);
                const convRate = parseFloat(row?.convRate);
                const dropRate = parseFloat(row?.dropRate);
                return (
                  <tr key={row?.source}
                    style={{ borderBottom: i < rows?.length - 1 ? '1px solid #dde1e7' : 'none', transition: 'background 150ms ease' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: sc?.dot, flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px', background: sc?.bg, color: sc?.color }}>{row?.source || 'direct'}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 600, color: '#1e2b38' }}>{row?.sessions?.toLocaleString('de-DE')}</td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 600, color: '#1e2b38' }}>{row?.bookings}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '6px', background: '#f5f6f8', borderRadius: '100px', overflow: 'hidden', minWidth: '60px' }}>
                          <div style={{ height: '100%', width: Math.min(convRate, 100) + '%', background: getConvColor(convRate), borderRadius: '100px' }} />
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: getConvColor(convRate), minWidth: '40px' }}>{row?.convRate}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '6px', background: '#f5f6f8', borderRadius: '100px', overflow: 'hidden', minWidth: '60px' }}>
                          <div style={{ height: '100%', width: Math.min(dropRate, 100) + '%', background: getDropColor(dropRate), borderRadius: '100px' }} />
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: getDropColor(dropRate), minWidth: '40px' }}>{row?.dropRate}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '100px',
                        background: convRate >= 25 ? 'rgba(22,163,74,0.1)' : convRate >= 15 ? 'rgba(217,119,6,0.1)' : 'rgba(220,38,38,0.1)',
                        color: convRate >= 25 ? '#16a34a' : convRate >= 15 ? '#d97706' : '#dc2626',
                      }}>
                        {convRate >= 25 ? 'Stark' : convRate >= 15 ? 'Mittel' : 'Schwach'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UtmDropOffTable;
